import { createContext, useContext, useRef, type ReactNode } from 'react';

/**
 * Puente entre el botón "volver" del StudentAreaWrapper y el estado del
 * perfil: la página registra si hay cambios sin guardar y cómo confirmar la
 * salida (modal), y el wrapper lo consulta antes de navegar.
 */
export interface ProfileNavGuardValue {
  /** ¿Hay cambios sin guardar? */
  isDirty: () => boolean;
  /** Abre la confirmación; llama a `proceed()` si el usuario decide salir. */
  confirmLeave: (proceed: () => void) => void;
}

interface ProfileNavGuardApi {
  setGuard: (guard: ProfileNavGuardValue | null) => void;
  getGuard: () => ProfileNavGuardValue | null;
}

const ProfileNavGuardContext = createContext<ProfileNavGuardApi | null>(null);

export function ProfileNavGuardProvider({ children }: { children: ReactNode }) {
  const guardRef = useRef<ProfileNavGuardValue | null>(null);
  const apiRef = useRef<ProfileNavGuardApi>({
    setGuard: (guard) => {
      guardRef.current = guard;
    },
    getGuard: () => guardRef.current,
  });
  return (
    <ProfileNavGuardContext.Provider value={apiRef.current}>
      {children}
    </ProfileNavGuardContext.Provider>
  );
}

export function useProfileNavGuard(): ProfileNavGuardApi {
  const ctx = useContext(ProfileNavGuardContext);
  if (!ctx) {
    throw new Error('useProfileNavGuard must be used within a ProfileNavGuardProvider');
  }
  return ctx;
}
